<template>
  <nav
    class="kpi-view-tabs"
    aria-label="Visões de faturamento"
  >
    <router-link
      v-for="aba in abas"
      :key="aba.name"
      :to="{ name: aba.name }"
      class="kpi-view-tabs__tab"
      :class="{ 'kpi-view-tabs__tab--active': aba.name === current }"
      :aria-current="aba.name === current ? 'page' : null"
    >
      <feather-icon
        :icon="aba.icon"
        size="15"
        class="mr-50"
      />
      {{ aba.label }}
    </router-link>
  </nav>
</template>

<script>
/**
 * As duas visões de faturamento, lado a lado.
 *
 * §34 do briefing: "No menu Faturamento, disponibilizar duas visões próximas:
 * Faturômetro; Evolução de Performance. Ao acessar uma delas, a outra deve
 * permanecer claramente disponível por aba ou navegação equivalente."
 *
 * A ata explica por que abas e não submenu: o componente de menu do Oráculo
 * pode não suportar mais um nível. Duas abas no topo das próprias telas
 * contornam isso sem depender de mudança no menu.
 *
 * São `router-link` e não botões de propósito — o usuário consegue abrir a
 * outra visão em aba nova com o meio do mouse, que é como se compara duas
 * telas de faturamento na prática.
 */
import { KPI, PEFORMANCE_EVOLUTION, READ_ACTION } from '@/constants/resources'

export default {
  name: 'KpiViewTabs',
  props: {
    /** Nome da rota ativa. */
    current: {
      type: String,
      required: true,
    },
  },
  computed: {
    /**
     * Só as abas que esta pessoa pode abrir.
     *
     * As duas telas têm recursos de ACL DIFERENTES — o Faturômetro é `kpi` e a
     * Evolução é `performance_evolution` — e havia gente vendo a segunda aba sem
     * poder entrar nela.
     *
     * Isto é DEFENSIVO, e não o conserto de um defeito observado — a correção
     * de um diagnóstico meu que estava errado. Eu vi um PN restrito ser jogado
     * em "Compromissos" ao tentar abrir a Evolução e atribuí à ACL. Não era:
     * medido depois, esse PN TEM `performance_evolution:read`, e clicar na aba
     * leva à tela. Quem o expulsava era o `beforeEach` do router, que dispara
     * `CHECK_APPOINTMENT_REPORT_PENDENCIES` e força quem tem relatório de
     * compromisso pendente para `/compromissos` — comportamento intencional de
     * toda a aplicação, não desta tela.
     *
     * A checagem fica porque é barata e está certa em princípio: se um dia
     * alguém perder o recurso, uma aba que parece levar a um lugar e leva a
     * outro é pior do que uma aba que não existe. Mas hoje nenhum perfil da base
     * cai nela.
     */
    abas() {
      return [
        {
          name: 'kpi-tree', label: 'Faturômetro', icon: 'GitMergeIcon', recurso: KPI,
        },
        {
          name: 'kpi-evolucao-performance',
          label: 'Evolução de performance',
          icon: 'TrendingUpIcon',
          recurso: PEFORMANCE_EVOLUTION,
        },
      ].filter(aba => this.$can(READ_ACTION, aba.recurso))
    },
  },
}
</script>

<style lang="scss" scoped>
.kpi-view-tabs {
  display: flex;
  gap: 0.25rem;
  margin-bottom: 1rem;
  border-bottom: 1px solid var(--kpi-line-strong, rgba(34, 41, 47, 0.08));
}

.kpi-view-tabs__tab {
  display: inline-flex;
  align-items: center;
  padding: 0.55rem 0.9rem;
  border-bottom: 2px solid transparent;
  /* As abas ficam sobre o fundo da pagina, sem card atras: #6e6b7b e o corpo
     do tema CLARO e some sobre #161d31. */
  color: var(--kpi-tab-fg, #6e6b7b);
  font-size: 0.929rem;
  font-weight: 500;
  white-space: nowrap;
  transition: color 160ms ease, border-color 160ms ease;

  &:hover {
    color: #f26237;
  }
}

.kpi-view-tabs__tab--active {
  color: #f26237;
  border-bottom-color: #f26237;
}

@media (max-width: 575.98px) {
  .kpi-view-tabs {
    overflow-x: auto;
  }

  .kpi-view-tabs__tab {
    padding: 0.5rem 0.6rem;
    font-size: 0.857rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .kpi-view-tabs__tab {
    transition: none;
  }
}
</style>
